/**
 * Canadian craft malthouses — Red Shed Malting, 3D Malt, Maker's Malt and
 * Scythe Malting on the prairies; Byrne Craft Malt and Mississippi Mills
 * Malting in Ontario; MaltBroue and Malterie Caux-Laflamme in Quebec; Horton
 * Ridge Malt and Island Malt House in the Maritimes.
 *
 * Same kind of company as the US houses in this directory, so the same helper
 * and the same `representative` default apply; every row passes
 * `origin: 'Canada'`. Canada's two industrial maltsters, Canada Malting and
 * Prairie Malt, are not here: they have files of their own and a national
 * reach.
 *
 * Canadian craft houses publish colour in SRM or ASBC °L, which are the same
 * number for malt, so `colorLovibond` is passed as published (band mid-points
 * where a band is printed) and EBC is derived. Order is west to east.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** Alberta, Saskatchewan, Manitoba, Ontario, Quebec, Nova Scotia and PEI craft malts. @see Fermentable */
export const canadaCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Red Shed Malting — Red Deer County, Alberta
  //
  // A family malthouse on the Hamill farm that grows its own barley, wheat and
  // rye and malts it in a drum system. They describe themselves as the first
  // craft malthouse in Alberta and the first malt roaster in Canada, and the
  // roaster is why the list runs from pilsner to 700 SRM. Parkland Pale won
  // Best in Show at the 2026 Malt Cup (CDC Churchill barley). Caribou took
  // silver in Caramel as "Caribou Malt – C40", and Whisky Jack took bronze in
  // Distillers, spelled "Whiskey Jack" in the results. The store spells it
  // "Whisky".
  //
  // Figures come from the "Malt Analysis" tab on each product page of their own
  // Shopify store. Colour is published as an SRM range and is entered here as
  // the midpoint, read as °L as the other craft files do. Fine extract and DP
  // are theirs, unrounded. **Moisture is never published**, so every moisture
  // below is class-typical: 4.5 % for kilned malt, 3 % for roasts, 12 % for raw
  // grain. Roasted Barley, Black Bird and Black Bear publish colour only, so
  // their 70 % extract is class-typical too. A "Malt Specifications" download
  // exists on the store and may carry more; it was not fetched.
  //
  // Crystal and roasted rows carry zero diastatic power because the catalog
  // requires it. Red Shed publish no DP for those rows, so nothing is contradicted.
  //
  // The raw grains publish DP (barley 75, wheat 63, rye 55). Unmalted grain does
  // carry some beta-amylase, and nothing in the tests forbids DP on
  // `adjunct-grain`, so the published figures stand.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'red-shed-penhold-pilsner',
    name: 'Penhold Pilsner',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 83,
    moisturePercent: 4.5,
    // Published 1.2–2.2 SRM.
    colorLovibond: 1.7,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Alberta pilsner malt with a wildflower and soft white bread character and a light honeyed finish. Built to be the whole grist of a lager or kolsch.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/penhold-pilsner',
  }),
  craftMalt({
    id: 'red-shed-parkland-pale',
    name: 'Parkland Pale',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 83,
    moisturePercent: 4.5,
    // Published 2.2–3.3 SRM.
    colorLovibond: 2.8,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pale malt from a barley bred for flavor rather than enzyme, with honey, almond and graham cracker notes. Its diastatic power is kept deliberately modest for all-malt grists. It won Best in Show at the 2026 Malt Cup.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/parkland-pale-base-malt',
  }),
  craftMalt({
    id: 'red-shed-whisky-jack',
    name: 'Whisky Jack',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // Published 1.4–2.5 SRM.
    colorLovibond: 2,
    diastaticPowerLintner: 200,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A high-enzyme base malt made for distilling and for grists heavy in adjuncts, with a mild biscuit and cereal flavor. Use it as the base when unmalted grain or flakes need converting.',
    tags: [
      'base',
      'high-dp',
      'distilling',
      'adjunct-friendly',
      'craft-malt',
      'alberta',
      'canadian',
    ],
    url: 'https://www.redshedmalting.ca/products/whisky-jack-base-malt',
  }),
  craftMalt({
    id: 'red-shed-rocky-mountain',
    name: 'Rocky Mountain',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    // Published 4–6 SRM.
    colorLovibond: 5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A Vienna-style high-kilned malt with bread crumb, light nut and honey-oat notes. It keeps enough enzyme to stand alone, or it can deepen a pale base.',
    tags: ['kilned', 'vienna', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/rocky-mountain-high-kilned-specialty-malt',
  }),
  craftMalt({
    id: 'red-shed-kananaskis',
    name: 'Kananaskis',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    // Published 7–11 SRM.
    colorLovibond: 9,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Munich replacement with fresh-bread sweetness and a cherry and raspberry note. Use a portion for depth in helles, dunkel or bock, or all of it, since it converts itself.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/kananaskis-high-kilned-specialty-malt',
  }),
  craftMalt({
    id: 'red-shed-wheat',
    name: 'Wheat Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    // Published 1.6–3 SRM.
    colorLovibond: 2.3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted spring red wheat from the family farm: dough, cracker and a slight sweetness. It adds body and a lasting head to wheat beers and hazy ales.',
    tags: ['base', 'wheat', 'head-retention', 'haze', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/wheat-malt-base-malt',
  }),
  craftMalt({
    id: 'red-shed-rye',
    name: 'Rye Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    // Published 2–3.5 SRM.
    colorLovibond: 2.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Malted rye with rye-bread spice and an earthy, honeyed edge. Up to about 20 percent in a roggenbier or rye IPA, and more in a rye whisky mash.',
    tags: ['rye', 'spicy', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/rye-malt',
  }),
  craftMalt({
    id: 'red-shed-oat',
    name: 'Oat Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 61,
    moisturePercent: 4.5,
    // Published 2.0–3.5 SRM.
    colorLovibond: 2.8,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted oats that go straight into the mash with no cereal cook, for a creamy, porridge-like body. Extract is low because the hull stays on, so keep it under about 30 percent.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/oat-malt-base-malt',
  }),
  craftMalt({
    id: 'red-shed-caribou',
    name: 'Caribou Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // Published 30–50 SRM; entered at the 2026 Malt Cup as "C40".
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A caramel malt made by drum-roasting wet green malt, with light caramel, brown sugar, honey and toffee. Use it where a crystal 40 would go in pale ales, IPAs and English ales.',
    tags: ['crystal', 'caramel', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/caribou-malt',
  }),
  craftMalt({
    // Typed kilned-toasted like the catalog's other biscuit malts, although
    // Red Shed drum-roast it: they suggest up to 25 % of the grist, and a
    // `roasted` row is capped at 20.
    id: 'red-shed-biscuit',
    name: 'Biscuit Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 3,
    // Published 13–22 SRM.
    colorLovibond: 17.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Drum-roasted biscuit malt: bread, graham cracker, black tea and honey. A little adds depth, and they suggest up to a quarter of the grist for an openly toasty beer.',
    tags: ['kilned', 'biscuit', 'toasty', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/biscuit-malt',
  }),
  craftMalt({
    id: 'red-shed-amber',
    name: 'Amber Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 3,
    // Published 40–60 SRM.
    colorLovibond: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A roasted amber with peanut, pistachio and toasted-wood notes. It adds color and a dry nuttiness in place of caramel malt, for milds, bitters, browns and porters.',
    tags: ['roasted', 'amber', 'nutty', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/amber-malt',
  }),
  craftMalt({
    id: 'red-shed-light-chocolate',
    name: 'Light Chocolate Malt',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 3,
    // Published 130–170 SRM.
    colorLovibond: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'The gentlest of their chocolate roasts: roasted marshmallow, light coffee and milk chocolate. It suits brown ales and porters that want roast flavor without the bite.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/light-chocolate-roasted-malt',
  }),
  craftMalt({
    id: 'red-shed-chocolate-300',
    name: 'Chocolate Malt 300',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 3,
    // Published 250–350 SRM.
    colorLovibond: 300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'A mid-color chocolate malt with coffee, cocoa and dark chocolate. Built for brown ales, porters and stouts, at up to about 10 percent.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/chocolate-malt',
  }),
  craftMalt({
    id: 'red-shed-chocolate-500',
    name: 'Chocolate Malt 500',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 3,
    // Published 450–550 SRM.
    colorLovibond: 500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: true,
    description:
      'The darker chocolate roast, with more coffee and dark chocolate than the 300. Use it in stouts and porters where the roast should lead.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/chocolate-malt-500',
  }),
  craftMalt({
    id: 'red-shed-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    // Published 500–600 SRM; extract is class-typical (not published).
    colorLovibond: 550,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'Roasted unmalted barley with intense coffee and cacao and low astringency. Enough flavor for a dry stout, and gentle enough to color a black lager. The extract figure is class-typical because the maker publishes color only.',
    tags: ['roasted', 'roasted-barley', 'stout', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/roasted-barley-roasted-malt',
  }),
  craftMalt({
    id: 'red-shed-black-bird',
    name: 'Black Bird',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    // Published 650–750 SRM; entered at 700, the schema ceiling. Extract is
    // class-typical (not published).
    colorLovibond: 700,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: true,
    description:
      'Their black malt: dark toast and cacao, dry, a medium roast. It adds color or balances sweetness, and pairs well with their chocolate malt. The extract figure is class-typical because the maker publishes color only.',
    tags: ['roasted', 'black-malt', 'color', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/black-bird',
  }),
  craftMalt({
    id: 'red-shed-black-bear',
    name: 'Black Bear',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    // Published 450–550 SRM; extract is class-typical (not published).
    colorLovibond: 500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: true,
    description:
      'Roasted wheat malt that gives a black color with little bitterness, plus light coffee and dark chocolate. Use small amounts to adjust color in a black IPA or schwarzbier. The extract figure is class-typical.',
    tags: [
      'roasted',
      'wheat',
      'color',
      'dehusked-alternative',
      'craft-malt',
      'alberta',
      'canadian',
    ],
    url: 'https://www.redshedmalting.ca/products/black-bear',
  }),
  craftMalt({
    id: 'red-shed-raw-barley',
    name: 'Raw Barley',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 81,
    moisturePercent: 12,
    // Published 2–3 SRM.
    colorLovibond: 2.5,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'The same two-row they malt, cleaned and bagged unmalted, with field and farmer information. It adds a grainy, bread-dough character and substitutes for flaked barley.',
    tags: ['barley', 'unmalted', 'adjunct', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/raw-barley',
  }),
  craftMalt({
    id: 'red-shed-raw-wheat',
    name: 'Raw Wheat',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 12,
    // Published 2–3 SRM.
    colorLovibond: 2.5,
    diastaticPowerLintner: 63,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Unmalted wheat for head retention, lacing and haze without extra body or sweetness. Use it in Belgian wheat styles and hazy beers in place of flaked wheat.',
    tags: ['wheat', 'unmalted', 'adjunct', 'witbier', 'hazy', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/raw-wheat',
  }),
  craftMalt({
    id: 'red-shed-raw-rye',
    name: 'Raw Rye',
    maker: 'Red Shed Malting',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 84,
    moisturePercent: 12,
    // Published 2 SRM.
    colorLovibond: 2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Fall-seeded rye, sized and color-sorted, with a rye-bread, spicy and earthy character. Sold mainly for distilling. In beer it is a spicy adjunct at modest rates.',
    tags: ['rye', 'unmalted', 'adjunct', 'distilling', 'craft-malt', 'alberta', 'canadian'],
    url: 'https://www.redshedmalting.ca/products/raw-rye',
  }),

  // ---------------------------------------------------------------------------
  // 3D Malt — Camrose, Alberta
  //
  // Four partners malting Alberta barley on a farm near New Norway. The site
  // says any lot can be traced to the field it was grown in. Their products page
  // gives only a name, an SRM range and a 25 kg bag for each malt. The colours
  // below are those ranges' midpoints. **Every other figure is class-typical**,
  // and each description says so.
  //
  // Two things a future editor must know:
  // - The page lists Munich as "SRM Range: 6 & 22", which reads as two kilnings
  //   sold under one name. Only the lighter one is here. If 3D confirm
  //   a darker Munich, it needs its own row and name. Do not stretch this one.
  // - The 2026 Malt Cup gold in Wheat went to "Wheat Pale" from 3D Malt. That
  //   is probably the product their page calls "Malted Wheat", but nothing
  //   published links the two names, so the row keeps the page's name and the
  //   medal stays in this comment.
  // A Camrose chamber-of-commerce shop has listed an "Alberta Honey Malt" from
  // them. It is not on their own page and has no row.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: '3d-malt-pilsner',
    name: 'Pilsner',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // Published 1.4–1.8 SRM.
    colorLovibond: 1.6,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pilsner malt from Alberta barley, traceable to the field. Use it as the whole grist of a lager or kolsch. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-2-row-base',
    name: '2 Row Base',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // Published 2.1–2.5 SRM.
    colorLovibond: 2.3,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A general-purpose Alberta two-row base for any ale or lager grist. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['base', '2-row', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-pale',
    name: 'Pale',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    // Published 2.4–3.1 SRM.
    colorLovibond: 2.8,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A pale ale malt a shade darker than their 2 Row Base, for ales that want a little more malt. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-vienna',
    name: 'Vienna',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    // Published 3.2–3.7 SRM — light for a Vienna, and kept as published.
    colorLovibond: 3.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Vienna at about 3.5 SRM, closer to a rich pale than a toasty Vienna, for Vienna lagers and maltier pale ales. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['kilned', 'vienna', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-munich',
    name: 'Munich',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    // The page says "SRM Range: 6 & 22"; this is the lighter grade. See the block.
    colorLovibond: 6,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A light Munich for malty depth in amber lagers and bocks. The maker also lists a darker grade under the same name. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-wheat',
    name: 'Malted Wheat',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // Published 1.5–1.9 SRM.
    colorLovibond: 1.7,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Pale Alberta wheat malt for body, head retention and haze, up to about 60 percent in a wheat beer. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['base', 'wheat', 'head-retention', 'haze', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-rye',
    name: 'Malted Rye',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    // Published 4.5 SRM.
    colorLovibond: 4.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Rye malt for spice and a slick body, at up to about 20 percent in a rye pale or roggenbier. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['rye', 'spicy', 'craft-malt', 'alberta', 'canadian'],
  }),
  craftMalt({
    id: '3d-malt-oats',
    name: 'Malted Oats',
    maker: '3D Malt',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 6,
    // Published 1.9 SRM.
    colorLovibond: 1.9,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted oats for a silky body in stouts and hazy ales. Keep them under about 30 percent because the hull yields little extract. The color is the maker’s, and the other values are class-typical because the maker lists the malt by name and color only.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'alberta', 'canadian'],
  }),

  // ---------------------------------------------------------------------------
  // Maker's Malt — Rosthern, Saskatchewan
  //
  // A farm collective in the Sask Valley that grows its own malting barley,
  // mostly Copeland, and malts it single-variety in Rosthern. They send
  // batches to Hartwick College for analysis. Every figure below is from the
  // "Batch Specifications" table on the product's own page. Ranges are entered
  // at the midpoint and floors (">120 L") at the floor. They also publish
  // 2019–2020 PDF data sheets that disagree with the pages in places. The pages
  // are used because they are what the shop links to.
  //
  // Some URL slugs do not match the product names, because products were
  // renamed in place: `bow-bayern` is Maker's English, `caramel-40` is Caramel
  // 30, `makers-esb-munich` is Light Munich, `makers-munich-20` is Dark Munich
  // and `regier-rye` is Hestia Rye. The urls are correct. Only the slugs are old.
  //
  // Three places where the data and the catalog's rules disagree:
  // - **Caramel 30 and Caramel 60 publish DP ">60 L".** The catalog holds every
  //   crystal malt at zero, so both rows are entered at 0. Maker's print >60;
  //   the figure is recorded here and not carried into the row.
  // - **Hestia Rye publishes "Fine Extract 90-92 %",** and the product copy
  //   repeats "92% extract". The row does not use it. The table names no
  //   basis (dry or as-is, fine or coarse), and none makes it plausible: the
  //   rye malts in this catalog run 75-85 %, the highest any maker prints is
  //   Caux-Laflamme's 87.5, and 91 would leave almost nothing in the grain
  //   that is not starch. The same page also disagrees with itself on
  //   beta-glucan (51 mg/L in the copy, ~120 in the table). So the row carries
  //   a class-typical 81 % and says so; colour, moisture and DP are theirs.
  //   Page re-fetched 2026-09-27 to check the figure and look for a basis.
  // - **Maker's Dark publishes one lot, not a spec**: moisture 2.2 %, 45 SRM,
  //   78.9 %, DP 28. Those single figures are used as published.
  //
  // Maker's Midnight (~80 SRM) is typed kilned-toasted, not roasted, because
  // Maker's publish DP ">60 L" for it and call it high-kilned. A roasted row
  // with enzyme would fail the tests, and they say it is kilned, not roasted.
  // Their Flaked Oats are a partner product from MGM Seed and Grain, not malt,
  // and are no longer in the shop, so they have no row.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'makers-premium-pale',
    name: "Maker's Premium Pale",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.8,
    moisturePercent: 5,
    // Published 1.8–2.8 SRM, 81–82.5 %, DP >120.
    colorLovibond: 2.3,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their best-selling base, from single-variety Saskatchewan Copeland: fresh bread, unsalted cracker and malty sweetness. Use it for any beer, up to the whole grist.',
    tags: ['base', 'pale', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-premium-pale/',
  }),
  craftMalt({
    id: 'makers-pilsner',
    name: "Maker's Pilsner",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.8,
    moisturePercent: 5,
    // Published 1.6–2.0 SRM, 81–82.5 %, DP >120.
    colorLovibond: 1.8,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Low-protein Copeland pilsner malt with grainy sweetness, honey and a floral edge. It is the backbone for a pilsner or any beer that must finish very pale.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-pilsner/',
  }),
  craftMalt({
    id: 'makers-english',
    name: "Maker's English",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.8,
    moisturePercent: 5,
    // Published 3–4 SRM, 81–82.5 %, DP >120.
    colorLovibond: 3.5,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their take on Maris Otter, made from CDC Bow barley when it is available and formerly sold as Bow Bayern: cereal, toasted cracker, nuts and sweet biscuit. Use it as a full-bodied English ale base.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/bow-bayern/',
  }),
  craftMalt({
    id: 'makers-vienna',
    name: "Maker's Vienna",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81.8,
    moisturePercent: 5,
    // Published 4–6 SRM, 81–82.5 %, DP >110.
    colorLovibond: 5,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The malthouse signature: bread crust, toffee and crackery sweetness. Use it as a whole grist or to add backbone to a pale malt, particularly in English-style beers.',
    tags: ['kilned', 'vienna', 'malty', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-vienna-mozart/',
  }),
  craftMalt({
    id: 'makers-light-munich',
    name: "Maker's Light Munich",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    // Published 8–12 SRM, 81–83 %, DP >80.
    colorLovibond: 10,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A light Munich with sweet-tea aromatics, bread crust, toffee and a black-tea tannin. It keeps enough enzyme for heavy use, up to half the grist in darker ales, lagers and bocks.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-esb-munich/',
  }),
  craftMalt({
    id: 'makers-dark-munich',
    name: "Maker's Dark Munich",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~20 SRM, 80–82 %, DP ~60.
    colorLovibond: 20,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A dark Munich balancing caramel sweetness against a light roast, with an Earl Grey aroma. It keeps a base-like enzyme and extract package, so it can be half the grist of a dunkel or bock.',
    tags: ['kilned', 'munich', 'dark-munich', 'malty', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-munich-20/',
  }),
  craftMalt({
    id: 'makers-honeyb',
    name: "Maker's HoneyB",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~20 SRM, 80–82 %, DP >100 (the 2020 PDF says >60).
    colorLovibond: 20,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A stewed honey-style malt with honey, light caramel and mild acidity, without the roast of their caramels. Use it at 5 to 20 percent for sweetness in blondes, ambers and browns.',
    tags: ['kilned', 'honey', 'sweet', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-honeyb/',
  }),
  craftMalt({
    id: 'makers-caramel-30',
    name: "Maker's Caramel 30",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~30 SRM, 80–82 %. DP published as >60 L; held at 0 (see block).
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A light-medium caramel malt from Copeland barley, with honey and caramel sweetness. Use it like any crystal 30 for color, body and head retention.',
    tags: ['crystal', 'caramel', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/caramel-40/',
  }),
  craftMalt({
    id: 'makers-caramel-60',
    name: "Maker's Caramel 60",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~60 SRM, 80–82 %. DP published as >60 L; held at 0 (see block).
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A medium caramel malt kilned harder than the 30, with darker caramel and molasses. Use it for color and body in ambers, reds and brown ales.',
    tags: ['crystal', 'caramel', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/caramel-60/',
  }),
  craftMalt({
    id: 'makers-dark',
    name: "Maker's Dark",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78.9,
    moisturePercent: 2.2,
    // Published as a single lot: 45 SRM, 78.9 %, DP 28, moisture 2.2 %.
    colorLovibond: 45,
    diastaticPowerLintner: 28,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Kilned past 250 °F, it sits between caramel sweetness and a burnt-roast bitterness, with coffee and chocolate. A specialty malt for color and dark flavor in browns and porters.',
    tags: ['kilned', 'high-kilned', 'dark', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-dark/',
  }),
  craftMalt({
    id: 'makers-midnight',
    name: "Maker's Midnight",
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~80 SRM, 80–82 %, DP >60.
    colorLovibond: 80,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'Their darkest high-kilned malt, made with an unusual kiln process: dark fruit like a red Bordeaux, then chocolate moving into roast. Stronger than it looks, so use it sparingly for color and dark flavor.',
    tags: ['kilned', 'high-kilned', 'dark', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/makers-midnight/',
  }),
  craftMalt({
    id: 'makers-hestia-rye',
    name: 'Hestia Rye',
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    // Published "90–92 %" with no basis; class-typical 81 used. See the block.
    extractFineDryPercent: 81,
    moisturePercent: 5,
    // Published ~6 SRM, DP ~60.
    colorLovibond: 6,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Organic rye grown by Hestia Farms near Laird and kilned for extra toast and spice, to a light amber like a dark Vienna. Very low beta-glucan. The maker suggests 5 to 20 percent in beer. Extract is class-typical: the published 90–92 percent gives no basis and is out of reach for rye malt.',
    tags: ['rye', 'spicy', 'organic', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/regier-rye/',
  }),
  craftMalt({
    id: 'makers-wheeler-wheat',
    name: 'Wheeler Wheat',
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.5,
    moisturePercent: 5,
    // Published 2–3 SRM, 80–85 %, DP >80, protein 13.5–15.5 %.
    colorLovibond: 2.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted hard red spring wheat instead of soft white, with a milling-wheat flavor and some phenolic notes. Its high protein brings a lot of haze and body.',
    tags: ['base', 'wheat', 'red-wheat', 'haze', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/wheeler-wheat-hard-red-spring-wheat/',
  }),
  craftMalt({
    id: 'makers-white-wheat',
    name: 'White Wheat',
    maker: "Maker's Malt",
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 5,
    // Published 2–3 SRM, ~85 %, DP >100.
    colorLovibond: 2.5,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted white wheat from Alberta, bought in and chosen to be free of Fusarium head blight. It has the enzyme for any use, from a traditional weissbier to haze in a pale ale.',
    tags: ['base', 'wheat', 'head-retention', 'haze', 'craft-malt', 'saskatchewan', 'canadian'],
    url: 'https://makersmalt.com/malts/white-wheat/',
  }),

  // ---------------------------------------------------------------------------
  // Scythe Malting Co. — Manitoba
  //
  // **Everything here comes from one Winnipeg retailer, not the maker.** Scythe
  // have no website. The Craft Maltsters Guild finder lists them by province
  // and links to an Instagram account. Grain to Glass in Winnipeg sell their
  // "2-Row Pale Malt" (in stock as of 2026-09), give a °L range for it, and
  // place Scythe in Springfield, Manitoba. The same shop once listed a
  // "Pilsner Malt"; that page survives only in the Wayback Machine (2025-08)
  // and is a 404 today, so it is not a row until a live listing names it. A local
  // homebrew club puts them west of Winnipeg, and Springfield is east, so the
  // town is left out of the header.
  //
  // The colour is the retailer's midpoint. **Extract, moisture and DP are
  // class-typical.** "High enzymatic power" is the retailer's phrase, not a
  // figure. The row carries no url, because the only product page is a
  // shop's. If Scythe publish a spec sheet, replace these numbers with theirs.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'scythe-2-row-pale',
    name: '2-Row Pale Malt',
    maker: 'Scythe Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    // 1.8–2.2 °L per the retailer.
    colorLovibond: 2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Manitoba-grown two-row malted in Manitoba, with a clean, mild flavor that stays out of the way of hops and specialty malts. Only color is published, by a retailer, so extract, moisture and enzyme values are class-typical.',
    tags: ['base', '2-row', 'craft-malt', 'manitoba', 'canadian'],
  }),

  // ---------------------------------------------------------------------------
  // Byrne Craft Malt — Essex, Ontario
  //
  // A floor malthouse on the Byrne family farm in Essex County, malting its own
  // two-row. The products page names three base malts and prints a price but
  // not a single analysis figure, so every number below is class-typical and
  // each row says so. What *is* published and used in the prose: all three are
  // floor-malted from barley grown in Essex County; Gesto Pilsner and Canadian
  // Pale are spring barley, Goldenfield Pale is winter barley. "Gesto" is the
  // hamlet the farm sits in. All three share one products page, so no row
  // carries a `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'byrne-gesto-pilsner',
    name: 'Gesto Pilsner',
    maker: 'Byrne Craft Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted two-row spring barley from Essex County, made for pilsners and light lagers: light color, a delicate malt sweetness and a crisp finish. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'ontario', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'byrne-goldenfield-pale',
    name: 'Goldenfield Pale',
    maker: 'Byrne Craft Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A pale gold base from floor-malted winter barley, with light toast and a little more malt presence than their other two bases, for pale ales, ambers and bitters. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'ontario', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'byrne-canadian-pale',
    name: 'Canadian Pale',
    maker: 'Byrne Craft Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their everyday two-row base, floor-malted from Essex County spring barley: clean and mild, a neutral foundation for pale ales, pilsners and lagers alike. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pale', 'two-row', 'craft-malt', 'ontario', 'floor-malted', 'local'],
  }),

  // ---------------------------------------------------------------------------
  // Mississippi Mills Malting (M3 Malts) — Pakenham, Ontario
  //
  // Malting since June 2021 from their own barley and neighbouring farms'. The
  // site is a single-page app, so the analysis was read out of its JavaScript
  // bundle, not a rendered page: extract, colour (SRM), protein, FAN and DP in
  // both °Lintner and DU for all seven malts. Bands are stored as mid-points.
  // Moisture is not published for any of them, so every row is
  // `representative` and carries 4–5 % as the class-typical figure; the other
  // three numbers are theirs.
  //
  // Left out: the unmalted distilling grains (corn, rye, wheat, sorghum, naked
  // oats — sold unmalted with no analysis) and Schuema pils, which they import
  // from Germany rather than make. De Falco's shop calls the English Pale Ale
  // Maris Otter; M3's own page does not name the variety, so neither do we.
  // One products page for the range, so no `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'm3-pilsner',
    name: 'Pilsner',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.5,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    diastaticPowerLintner: 155,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A clean, crisp Ontario pilsner malt at 1.8–2.0 SRM with plenty of enzyme, for pale lagers and light ales. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'ontario', 'local', 'high-enzyme'],
  }),
  craftMalt({
    id: 'm3-2-row-pale-ale',
    name: '2-Row Pale Ale',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 4.5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 155,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A neutral, slightly sweet two-row base at 2.0–2.2 SRM that carries a whole ale grist. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['base', 'pale', 'two-row', 'ale', 'craft-malt', 'ontario', 'local'],
  }),
  craftMalt({
    id: 'm3-english-pale-ale',
    name: 'English Pale Ale',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 4,
    colorLovibond: 3.8,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A British-style ale malt at 3.5–4.0 SRM with bready notes and a gentle sweetness, the base for bitters and English ales. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'ontario', 'local'],
  }),
  craftMalt({
    id: 'm3-vienna',
    name: 'Vienna',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81.5,
    moisturePercent: 4,
    colorLovibond: 5.5,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Slightly sweet and biscuity with a toasty edge, at 5–6 SRM, for Vienna lagers and amber ales; it still converts itself. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['kilned', 'vienna', 'lager', 'craft-malt', 'ontario', 'local'],
  }),
  craftMalt({
    id: 'm3-light-munich',
    name: 'Light Munich',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 8,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Munich at 8 SRM that adds body and a smooth malt depth without much color; 10–30 percent in ambers and lagers, more in a Munich-forward beer. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'ontario', 'local'],
  }),
  craftMalt({
    id: 'm3-malted-wheat',
    name: 'Malted Wheat',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 170,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Ontario wheat malt at 2.1 SRM and 15.6 percent protein, for a silky mouthfeel in hefeweizen, witbier and other wheat beers. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['base', 'wheat', 'head-retention', 'craft-malt', 'ontario', 'local'],
  }),
  craftMalt({
    id: 'm3-distillers-malt',
    name: 'Distillers Malt',
    maker: 'Mississippi Mills Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82.5,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 180,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A very pale, high-enzyme malt at 160–200 °Lintner, made for distillers; in a brewery it converts a heavy load of unmalted adjuncts. Extract, color and diastatic power are published; moisture is class-typical.',
    tags: ['base', 'distilling', 'high-enzyme', 'craft-malt', 'ontario', 'local'],
  }),

  // ---------------------------------------------------------------------------
  // MaltBroue — Témiscouata, Bas-Saint-Laurent, Quebec
  //
  // A farm malthouse since 2008 and, by its own account, still the only
  // caramel-malt maker in Quebec. The Cara Unic caramels are roasted in a
  // rotating kiln of Dany Bastille's design. Descriptions are translated from
  // the French pages, which are the complete ones: the English side leaves out
  // Malt de l'Est, the oat flakes and the chit malt.
  //
  // Published analyses: Malt de l'Est (colour band, moisture, extract band, DP
  // minimum) and the oat flakes (moisture, extract, colour) — both
  // `spec-sheet`. The Cara Unic grades publish colour (ASBC) and usage rates
  // only; extract and moisture are class-typical, so those rows are
  // `representative`, and so are the rye and raw wheat, which publish a colour
  // band or a protein figure and nothing else. Usage rates quoted in the prose
  // are MaltBroue's, and the `suggestedMaxPercent` values follow them.
  //
  // Malt de l'Est is sold only to eastern-Quebec brewers; it is here because a
  // brewer there can buy it. Chit malt is left out: its sheet says
  // "information to come". Blé Cara Unic's page quotes the current lot at 23 °L
  // (May 2026); the row stores the 20–40 band's mid-point, not the lot.
  //
  // `url` only where a product has a page of its own. The seven barley Cara
  // Unic grades share one table and carry none; the Blé Cara Unic page also
  // describes the rye caramel, and the link goes to the wheat, whose page it is.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'maltbroue-malt-de-lest',
    name: "Malt de l'Est",
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 125,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'A two-row pale malt from Synergy barley grown on the farm or as close to the malthouse as possible, with a lightly sweet bread flavor and an almond note; a whole-grist base for any style. Sold only to brewers in eastern Quebec.',
    tags: ['base', 'pale', 'two-row', 'craft-malt', 'quebec', 'local', 'synergy'],
    url: 'https://www.maltbroue.com/malt-de-lest-fiche-description/',
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-20',
    name: 'Cara Unic 20',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The palest Cara Unic: body, foam and a light honey-caramel sweetness. MaltBroue suggest 5 percent in a pilsner, up to 10 in a lager, 10–15 in a pale ale and up to 20 in a dark ale. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'light', 'honey', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-40',
    name: 'Cara Unic 40',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Soft caramel and honey with added body and head retention, grouped by the maker with the 20: 5 percent in a pilsner up to 20 in a dark ale. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-60',
    name: 'Cara Unic 60',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'A richer caramel aroma and flavor and a rounder mouthfeel than the paler grades. MaltBroue suggest 1–10 percent in pilsners and dark lagers, 10–20 in strong, deeply colored beers. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'medium', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-80',
    name: 'Cara Unic 80',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Pronounced caramel and a smooth, full mouthfeel, for ambers, dark lagers and strong beers at 10–20 percent, or a few percent for color. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'dark', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-100',
    name: 'Cara Unic 100',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The darkest of the rich-caramel band: deep caramel and a round body for strong, deeply colored beers at up to 20 percent. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'dark', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-120',
    name: 'Cara Unic 120',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Burnt caramel with a noticeable bitterness; up to 10 percent adds color and depth to red, brown and black beers. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'dark', 'burnt-sugar', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-cara-unic-160',
    name: 'Cara Unic 160',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 160,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The darkest Cara Unic: burnt caramel and a firmer bitterness, used at up to 10 percent for color and flavor in brown and black beers. Color is published; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'extra-dark', 'burnt-sugar', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-ble-cara-unic',
    name: 'Blé Cara Unic',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Caramel wheat at 20–40 °L: a golden color, caramelized-bread flavor, roundness and better foam, for helles, hefeweizen and dunkelweizen. Color is the published band’s mid-point; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'wheat', 'head-retention', 'craft-malt', 'quebec'],
    url: 'https://www.maltbroue.com/fiche-description-ble-cara-unic/',
  }),
  craftMalt({
    id: 'maltbroue-seigle-cara-unic',
    name: 'Seigle Cara Unic',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    description:
      'Caramel rye at 40–60 °L: rye’s spice with a sweet caramel note, for red and brown beers, usually around 10 percent and sometimes up to 25. Special order only. Color is the published band’s mid-point; extract and moisture are class-typical.',
    tags: ['crystal', 'caramel', 'rye', 'spicy', 'craft-malt', 'quebec'],
  }),
  craftMalt({
    id: 'maltbroue-seigle-malte',
    name: 'Seigle Malté',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted rye at 3–5 °L, spicy in a rye beer and the backbone of rye whisky. Past 20 percent its beta-glucan makes for a sticky mash, so use rice hulls; 1–10 percent adds character to other styles. Color is published; the other values are class-typical.',
    tags: ['base', 'rye', 'spicy', 'distilling', 'craft-malt', 'quebec'],
    url: 'https://www.maltbroue.com/en/malted-rye-fact-sheets/',
  }),
  craftMalt({
    id: 'maltbroue-flocons-davoine',
    name: "Flocons d'Avoine",
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80.3,
    moisturePercent: 9,
    colorLovibond: 1.05,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Pregelatinized Quebec oat flakes that go straight into the mash with no cereal cook. They add a lot of body and roundness, typically at 5–25 percent.',
    tags: ['adjunct', 'oats', 'flaked', 'body', 'craft-malt', 'quebec'],
    url: 'https://www.maltbroue.com/fiche-descriptive-flocons/',
  }),
  craftMalt({
    id: 'maltbroue-ble-cru',
    name: 'Blé Cru',
    maker: 'MaltBroue',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 10,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Raw Quebec hard red wheat at 14 percent protein for Belgian-style white beers: long-lasting foam and more body and wheat flavor than soft wheat, at the cost of a slower runoff. Protein is published; the other values are class-typical.',
    tags: ['adjunct', 'wheat', 'unmalted', 'witbier', 'head-retention', 'craft-malt', 'quebec'],
    url: 'https://www.maltbroue.com/en/raw-wheat-fact-sheets/',
  }),

  // ---------------------------------------------------------------------------
  // Malterie Caux-Laflamme — Saint-Patrice-de-Beaurivage, Quebec
  //
  // A family malthouse on a former dairy farm, malting Quebec grain from its
  // own fields and about forty partner farms. It began in 2015 as the first
  // franchised farm-malthouse of MaltBroue's network and has long since gone
  // its own way; the plant burned in March 2024 and a rebuilt, automated one
  // opened in June 2025. Every grain is also sold certified organic under a
  // separate sheet — only the conventional sheets were read, and the organic
  // lots are not rows of their own.
  //
  // Figures come from the per-product technical sheets ("V1 01-2025", a
  // "typical analysis" per product, linked from each product page as a Google
  // Drive file), translated from French. Colour is printed as SRM, which the
  // product pages repeat as ASBC °L. Where a sheet gives moisture as a ceiling
  // (≤ 5.0 %) the ceiling is stored.
  //
  // Where page and sheet disagree, the sheet wins, as everywhere else:
  //  - Honey Malt: page 14.6–17.5 °L, sheet 24.0 (and a 19–23 band).
  //  - Malt Ambré: page 11.6–19.0 °L, sheet 24.5. The page's band is the one
  //    Munich Foncé also carries, so it looks copied.
  //  - Seigle malté prints 87.5 % fine-grind extract, more than barley
  //    reaches. It is stored as published: rye has no husk, so it yields more
  //    per kilogram than barley, and the extract band in
  //    craft-consistency.test.ts allows the `wheat` class (wheat and rye) up
  //    to 88 for that reason.
  //  - Blé Munich's sheet is a half-filled template: colour 12.64 SRM is kept,
  //    but its "Extra fine 59,7 %" sits beside a "Moisissure 1,6 %" line and is
  //    not a believable wheat-Munich extract, so extract and DP are
  //    class-typical and the row is `representative`.
  //
  // No sheet at all for Munich Foncé, the naked oats and the oat flakes; the
  // hulled-oat and malted-corn sheets omit extract and/or enzyme. Those rows
  // take the page's colour band mid-point and class-typical figures for the
  // rest, and say so. Épeautre malté (spelt) is left out: its page is written
  // for bakers and gives no colour or brewing use.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'caux-laflamme-malt-pilsner',
    name: 'Malt Pilsner',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.5,
    moisturePercent: 5.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Plump, low-protein Quebec two-row kilned pale for a clean grain flavor with bread and hay, for lagers and crisp beers. A whole-grist base with enzyme to spare.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-pilsner/',
  }),
  craftMalt({
    id: 'caux-laflamme-pilsner-traditionnel',
    name: 'Malt Pilsner Traditionnel',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82.5,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 114,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'A less-modified take on their pilsner malt, sold on the product page as “Pilsner façon Caux-Laflamme”: more body and foam and fewer DMS precursors, with grain, hay and a floury note. Suited to a step mash for a traditional lager.',
    tags: ['base', 'pilsner', 'lager', 'undermodified', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-pilsner-traditionnel/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-pale',
    name: 'Malt Pâle',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Their first recipe: a pale base with bread, cereal and a light honey note that gives a pale ale more character than a neutral two-row.',
    tags: ['base', 'pale', 'ale', 'honey', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-pale/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-anglais',
    name: 'Malt Anglais',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'An English-style pale ale malt with rich toasted bread and tea-biscuit sweetness, the base for bitters, milds and other English-inspired ales.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-anglais/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-vienna',
    name: 'Malt Vienna',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 82.5,
    moisturePercent: 4.5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 106.5,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Lightly golden with barley sugar, toast and light caramel. Enzymatic enough to be the whole grist of a Vienna lager or a malty amber.',
    tags: ['kilned', 'vienna', 'lager', 'malty', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-vienna/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-munich',
    name: 'Malt Munich',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81.5,
    moisturePercent: 4.5,
    colorLovibond: 11,
    diastaticPowerLintner: 81.5,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Made from higher-protein barley for a rich malt and caramel flavor with biscuit and cracker, and an amber color. It converts itself, so it can carry a dunkel or a bock, or add depth to an amber at 10–30 percent.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-munich/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-munich-fonce',
    name: 'Malt Munich Foncé',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 15.3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Their Munich kilned darker, with brown sugar, caramel, dry biscuit and toast, for dunkel, bock and brown ales. Color is the product page’s band mid-point; extract, moisture and diastatic power are class-typical, since no technical sheet is published.',
    tags: ['kilned', 'munich', 'dark-munich', 'malty', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/en/dark-munich-malt/',
  }),
  craftMalt({
    id: 'caux-laflamme-malt-ambre',
    name: 'Malt Ambré',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 24.5,
    diastaticPowerLintner: 62,
    suggestedMaxPercent: 20,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'An amber malt with almond, dry biscuit and crouton, for adding a layer of complexity to brown ales and darker beers. The sheet still reports some enzyme, but plan on it being carried by the base malt.',
    tags: ['kilned', 'amber', 'biscuit', 'nutty', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-ambre/',
  }),
  craftMalt({
    id: 'caux-laflamme-honey-malt',
    name: 'Honey Malt',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.5,
    moisturePercent: 4,
    colorLovibond: 24,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 15,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Honeyed sweetness with light caramel, pretzel and lightly toasted bread, red highlights and a slight acidity. A few percent brings out a honey note in a blonde or brown ale.',
    tags: ['kilned', 'honey', 'sweet', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/malt-honey/',
  }),
  craftMalt({
    id: 'caux-laflamme-chit-malt',
    name: 'Chit Malt',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 89,
    suggestedMaxPercent: 15,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Barley stopped just after sprouting, with a raw-grain, hay and floury flavor; used for body and foam at 5–15 percent rather than as a base.',
    tags: ['base', 'chit', 'head-retention', 'body', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/chit-malt/',
  }),
  craftMalt({
    id: 'caux-laflamme-ble-malte',
    name: 'Blé Malté',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 85.5,
    suggestedMaxPercent: 60,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'High-protein red wheat grown on the Caux-Laflamme farm itself, with sweet bread dough and a sourdough note. Improves foam and roundness in wheat beers.',
    tags: ['base', 'wheat', 'head-retention', 'craft-malt', 'quebec', 'estate'],
    url: 'https://malteriecauxlaflamme.com/ble-malte-2/',
  }),
  craftMalt({
    id: 'caux-laflamme-ble-munich',
    name: 'Blé Munich',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 6,
    colorLovibond: 12.6,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Wheat kilned like a Munich, with fresh bread, nuts, honey and caramel, for dunkelweizen, weizenbock and strong ales. Color and moisture are from the technical sheet; extract and diastatic power are class-typical.',
    tags: ['kilned', 'wheat', 'munich', 'malty', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/ble-munich/',
  }),
  craftMalt({
    id: 'caux-laflamme-mais-malte',
    name: 'Maïs Malté',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 66,
    moisturePercent: 6,
    colorLovibond: 1.1,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Very pale malted corn with a sweet, tortilla-like flavor that lightens the body and mutes the cereal note in light lagers and cream ales. Color, extract and moisture are published; diastatic power is class-typical.',
    tags: ['adjunct', 'corn', 'malted', 'cream-ale', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/mais-malte/',
  }),
  craftMalt({
    id: 'caux-laflamme-seigle-malte',
    name: 'Seigle Malté',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 87.5,
    moisturePercent: 5.5,
    colorLovibond: 4,
    diastaticPowerLintner: 137,
    suggestedMaxPercent: 50,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Malted rye with an intense, dry, spicy rye-bread flavor that also firms up the foam. A rye ale takes 20–40 percent, with rice hulls for the mash.',
    tags: ['base', 'rye', 'spicy', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/seigle-malte/',
  }),
  craftMalt({
    id: 'caux-laflamme-avoine-entiere-maltee',
    name: 'Avoine Entière Maltée',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 4.7,
    colorLovibond: 2.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Hull-on malted oats with a sweet porridge flavor and a silky texture; the hull stays on, which helps the mash drain. Color and moisture are published; extract and diastatic power are class-typical for hulled oat malt.',
    tags: ['adjunct', 'oats', 'malted', 'body', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/avoine-entiere-maltee/',
  }),
  craftMalt({
    id: 'caux-laflamme-avoine-nue-maltee',
    name: 'Avoine Nue Maltée',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 6,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Malted naked (hulless) oats for a silky texture, creamy foam and a soft, round sweetness in stouts, porters and New England IPAs. Color is the product page’s band mid-point; the other values are class-typical.',
    tags: ['adjunct', 'oats', 'naked-oats', 'malted', 'body', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/avoine-nue-maltee/',
  }),
  craftMalt({
    id: 'caux-laflamme-avoine-nue-maltee-doree',
    name: 'Avoine Nue Maltée Dorée',
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Golden-kilned naked oat malt with toasted cereal and a subtle nuttiness, adding soft texture and foam to stouts, porters and New England IPAs. Color is the page’s 6–12 °L band mid-point; the other values are class-typical, with no enzyme counted.',
    tags: ['kilned', 'oats', 'naked-oats', 'nutty', 'body', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/avoine-nue-maltee-doree/',
  }),
  craftMalt({
    id: 'caux-laflamme-flocon-davoine',
    name: "Flocon d'Avoine",
    maker: 'Malterie Caux-Laflamme',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70,
    moisturePercent: 9,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Large steamed flakes of whole white oat kernels, cream-colored with a porridge flavor, for body in stouts and hazy ales. All values shown are class-typical; the product page’s color line is garbled and no technical sheet is published.',
    tags: ['adjunct', 'oats', 'flaked', 'body', 'craft-malt', 'quebec'],
    url: 'https://malteriecauxlaflamme.com/flocon-davoine/',
  }),

  // ---------------------------------------------------------------------------
  // Horton Ridge Malt & Grain — Hortonville, Nova Scotia
  //
  // Atlantic Canada's first malthouse (2016) and a brewery since 2017, which
  // sells its certified-organic floor malt to other Nova Scotia brewers. The
  // site is mostly the taproom's and publishes no product list and no numbers,
  // so everything below is class-typical and each row says so.
  //
  // The names are sourced one by one. Vienna and Munich are named on their own
  // beer page ("our three most highly kilned malts (Vienna, Munich & Cara)");
  // Pale is named in Tidehouse's grist on the Atlantic Canada Beer Blog (Aug
  // 2020); The Otter Guys, a Maris Otter-style ale malt, in the same blog's
  // February 2024 wrap-up. Their "Cara" is left out: nothing says what colour
  // it is, and a crystal's colour is the whole of its data. Organic and
  // floor-malted are the maker's own words. The grain is mostly Saskatchewan
  // organic plus their own fields, so the prose does not call it Nova Scotia
  // grain.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'horton-ridge-pale',
    name: 'Pale Malt',
    maker: 'Horton Ridge Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Certified organic floor-malted pale barley, the base Nova Scotia brewers build pale ales and IPAs on. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'nova-scotia', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'horton-ridge-the-otter-guys',
    name: 'The Otter Guys',
    maker: 'Horton Ridge Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'An organic floor-malted ale malt in the Maris Otter manner, for bitters, milds and English-leaning pale ales. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'nova-scotia', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'horton-ridge-vienna',
    name: 'Vienna',
    maker: 'Horton Ridge Malt',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Organic floor-malted Vienna, one of the three kilned malts behind their copper Malt House Ale; for Vienna lagers, ambers and a toastier pale ale. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['kilned', 'vienna', 'craft-malt', 'nova-scotia', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'horton-ridge-munich',
    name: 'Munich',
    maker: 'Horton Ridge Malt',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Organic floor-malted Munich for malty depth and color in ambers, Märzen and altbier-style ales. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'nova-scotia', 'floor-malted', 'organic'],
  }),

  // ---------------------------------------------------------------------------
  // Island Malt House — Prince Edward Island
  //
  // A floor malthouse selling to brewers and distillers across Atlantic Canada,
  // from grain grown in PEI, New Brunswick and Nova Scotia. Their products page
  // names the range in prose and prints a colour only for the toasted wheat
  // (10 SRM) and the two dry-roasted malts (biscuit 15–17 SRM, amber 40 SRM,
  // 2–3 % moisture); everything else is class-typical, and every row is
  // `representative`. Each batch is analysed by Hartwick College, but the
  // results are not published.
  //
  // Naming: the page groups the last two under "Dry Roasted Malt" and calls
  // them a "Belgian biscuit malt" and "an amber malt", so the rows are Biscuit
  // Malt and Amber Malt. Their usage rates (1–10 % and 1–3 %) are the
  // `suggestedMaxPercent`. Buckwheat is left out: "limited availability", and
  // the page does not say whether it is sold malted or raw.
  // One products page for everything, so no `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'island-malt-pilsner',
    name: 'Pilsner',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted Maritime barley kilned pale for lagers and crisp ales. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-2-row',
    name: '2 Row',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted two-row from PEI, New Brunswick and Nova Scotia grain, a general-purpose base for ales. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'pale', 'two-row', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-vienna',
    name: 'Vienna',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 3.8,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted Vienna for a toasty, golden lager or a maltier pale ale; it converts itself. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['kilned', 'vienna', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-wheat',
    name: 'Wheat Malt',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted from locally grown hard red and winter wheat, for head retention and a soft grain flavor in wheat beers and hazy ales. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'wheat', 'head-retention', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-toasted-wheat',
    name: 'Toasted Wheat',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 10,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Wheat malt toasted to 10 SRM for a toasty sweetness in darker wheat beers and ambers. Color is published; the other values are class-typical.',
    tags: ['kilned', 'wheat', 'toasty', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-rye',
    name: 'Rye Malt',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'From a low beta-glucan malting rye: a little gives toffee notes and a reddish hue, more gives a long, spicy finish. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'rye', 'spicy', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-oat',
    name: 'Oat Malt',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 65,
    moisturePercent: 6,
    colorLovibond: 2.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Floor-malted oats for a fuller mouthfeel with toasted-biscuit aromas and flavors. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['adjunct', 'oats', 'malted', 'body', 'craft-malt', 'pei', 'floor-malted', 'local'],
  }),
  craftMalt({
    id: 'island-malt-biscuit',
    name: 'Biscuit Malt',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 3,
    colorLovibond: 16,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'A Belgian-style biscuit malt dry-roasted at 130 °C from kilned malt, with an orange hue and biscuit, toast and nut; 1–10 percent. Color is published; extract and moisture are class-typical.',
    tags: ['kilned', 'biscuit', 'toasty', 'nutty', 'craft-malt', 'pei', 'local'],
  }),
  craftMalt({
    id: 'island-malt-amber',
    name: 'Amber Malt',
    maker: 'Island Malt House',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 2.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 3,
    needsMashing: true,
    description:
      'Pale malt dry-roasted to 180 °C: dry, bready, nutty and toasted, bringing brown to copper color to milds, brown ales, porters and stouts at the maker’s suggested 1–3 percent. Color and moisture are published; extract is class-typical.',
    tags: ['kilned', 'amber', 'toasty', 'nutty', 'craft-malt', 'pei', 'local'],
  }),
];
